/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Row, Collapse } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { getSaveFile } from 'redux/actions/notificationActions';

import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';

import { Checkbox } from 'shared/components/form/CheckBox';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';

import { FolderListWrapper, ItemDataWrapper } from './styles';

const PATH_ID = '1/';

const FolderList = ({ items, currentFile, clientId, onClose }) => {
  const { customAreaApi } = useApi();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cusNavId, mail, name } = useSelector((state) => state.auth);
  const { saveFiles } = useSelector((state) => state.notification);
  const [folders, setFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [showError, setShowError] = useState(false);
  const [folderRoot, setFolderRoot] = useState({});

  const [{ loading: loadingCreate }, createDocument] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  useEffect(() => {
    setFolderRoot(items.find((x) => x.pathId === PATH_ID));
    setFolders(items.filter((x) => !x.isFile));
  }, [items]);

  const getCaretIcon = (item) => {
    if (
      (item.childsFolders && item?.childsFolders?.length > 0) ||
      (item.childs && item?.childs?.length > 0)
    ) {
      return item.isOpen ? <CaretUpIcon className="caret" /> : <CaretDownIcon className="caret" />;
    }
    return null;
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('documents_add_document_folder')}</h3>
        </Col>
      </Row>
    );
  };

  const getCurrentItem = (itemId) => {
    setFolders(
      folders.map((item) => (item.id === itemId ? { ...item, isOpen: !item.isOpen } : item))
    );
  };

  const removeFile = (folderId) => {
    const newFolders = selectedFolders.filter((item) => item.pathId !== folderId);
    setSelectedFolders(newFolders);
  };

  const selectedClientFolder = useCallback((e, item) => {
    const findFolder = selectedFolders.find((x) => x.pathId === item.pathId);
    if (!findFolder) {
      const folder = {
        createdBy: {
          nameComplete: name,
          mail,
        },
        pathId: item.pathId,
        parentPathId: item.pathId,
        name: currentFile.name,
        permissions: [
          {
            u: mail,
            p: 'rw-',
          },
        ],
        data: currentFile.data,
        size: currentFile.size,
        state: '',
        tags: [],
      };
      const newFolders = [folder];
      setSelectedFolders(newFolders);
    } else {
      removeFile(findFolder.pathId);
    }
  });

  const ItemTree = ({ listItem, level }) => {
    return listItem
      ? listItem.map((item) =>
          item.id ? (
            <React.Fragment key={item.id}>
              <ItemDataWrapper
                className="item-data-container"
                level={level}
                onClick={() => getCurrentItem(item.id)}
              >
                <div id={`toggle-folder${item.id}`} className="info-container">
                  <Checkbox
                    type="checkbox"
                    id="filterCheckAll"
                    label=""
                    checked={selectedFolders.some((file) => file.pathId === item.pathId)}
                    onClick={(e) => selectedClientFolder(e, item)}
                  />
                  <span className="text-file">{item.name}</span>
                </div>
                {getCaretIcon(item)}
              </ItemDataWrapper>
              <Collapse isOpen={item.isOpen}>
                {folders?.some((currentItem) => {
                  const pathIds = currentItem.pathId.split('/');
                  const clearedPathIds = pathIds.filter((x) => x.length > 0);
                  const parentId = clearedPathIds[clearedPathIds.length - 2];
                  return parentId === item.id;
                }) ? (
                  <ItemTree
                    listItem={folders?.filter((currentItem) => {
                      const pathIds = currentItem.pathId.split('/');
                      const clearedPathIds = pathIds.filter((x) => x.length > 0);
                      const parentId = clearedPathIds[clearedPathIds.length - 2];
                      return parentId === item.id;
                    })}
                    level={level + 1}
                  />
                ) : null}
              </Collapse>
            </React.Fragment>
          ) : null
        )
      : null;
  };

  const onCreateDocument = async () => {
    try {
      const {
        data: { data, dataErrors },
      } = await createDocument({
        data: selectedFolders,
        url: `${window.API_V1}/cli/items/${cusNavId}/${clientId}/${encodeURIComponent(mail)}`,
      });
      dispatch(getSaveFile([...saveFiles, ...data]));
      onClose();
      if (dataErrors?.length) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      console.error('error save document: ', error);
      setShowError(true);
    }
  };

  return (
    <FolderListWrapper>
      <Title />
      <div className="item-tree-container">
        <ItemTree
          listItem={folders?.filter((item) => {
            const pathIds = item.pathId.split('/');
            const clearedPathIds = pathIds.filter((x) => x.length > 0);
            const parentId = clearedPathIds[clearedPathIds.length - 2];
            return parentId === folderRoot.id;
          })}
          level={1}
        />
      </div>
      <div className="action-folder-list">
        <Button
          className="text-uppercase"
          type="button"
          color="primary"
          label={t('general_add')}
          onClick={onCreateDocument}
          disabled={selectedFolders.length === 0}
        />
      </div>
      {loadingCreate && <MainSpinner />}
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('custom_field_operation_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
    </FolderListWrapper>
  );
};

FolderList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  clientId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default FolderList;
